import { PermissionMetadataItem } from "../../../api";
import { DataModel } from "../../../models/data-model";
import {
  addPermissionWithImplied,
  conflictsWithSelection,
  getPermissionState,
  isDerivedState,
  type PermissionRules,
  type PermissionState,
  removePermissionWithDependents,
} from "./permissions-utils";

/**
 * DataModel for the permissions picker. Overrides the base selection methods so
 * DataTable's built-in selection column automatically applies the implied /
 * forbidden rules: selecting a permission pulls in its implied bits and drops
 * conflicting ones, and deselecting removes any bits that depended on it.
 */
export class PermissionsModel extends DataModel<PermissionMetadataItem> {
  constructor(
    data: PermissionMetadataItem[],
    private rules: PermissionRules,
  ) {
    super(data);
    this.idAttr = "bit";
    this.selectionMode = "multi";
  }

  override select(id: number, _exclusive: boolean) {
    this.selected = addPermissionWithImplied(id, this.selected, this.rules);
    if (this.selectionMode) this.computeData();
  }

  override deselect(id: number) {
    this.selected = removePermissionWithDependents(
      id,
      this.selected,
      this.rules,
    );
    if (this.selectionMode) this.computeData();
  }

  /**
   * Adds every permission that doesn't conflict with those added before it,
   * rather than letting a later one drop an earlier one. The table lists the
   * selected rows first, so select-all keeps the current selection.
   */
  override selectAllItems(items: PermissionMetadataItem[]) {
    let selection: number[] = [];
    for (const item of items) {
      if (conflictsWithSelection(item.bit, new Set(selection), this.rules)) {
        continue;
      }
      selection = addPermissionWithImplied(item.bit, selection, this.rules);
    }
    this.selected = selection;
    if (this.selectionMode) this.computeData();
  }

  stateOf(bit: number): PermissionState {
    const selected = new Set(this.selected);
    // Excluded, too, when only a bit it implies conflicts: checking it would
    // drop part of the selection all the same.
    if (
      !selected.has(bit) &&
      conflictsWithSelection(bit, selected, this.rules)
    ) {
      return "forbidden";
    }
    return getPermissionState(bit, selected, this.rules);
  }

  isSelectDisabled = (item: PermissionMetadataItem) => {
    return isDerivedState(this.stateOf(item.bit));
  };
}
